import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, CheckCircle, UserCheck, Search, Loader2, DollarSign, CalendarCheck, Trophy, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { VentureCard, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { InputVenture } from "@/components/ui/input-venture";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAllStageMappings } from "@/hooks/useStageMapping";
import { PageTour } from "@/components/tour/PageTour";
import { pipelineSteps } from "@/lib/tour-steps";
import { CATEGORY_BADGE_VARIANTS, CATEGORY_LABELS, isCategoryAtOrDeeper } from "@/lib/stage-categories";

interface FunnelEvent {
  id: string;
  client_id: string;
  ghl_contact_id: string;
  ghl_opportunity_id: string | null;
  contact_name: string | null;
  stage: string;
  deal_value_cents: number | null;
  setter_id: string | null;
  setter_name: string | null;
  lead_created_at: string | null;
  created_at: string;
}

interface ImportedLead {
  id: string;
  client_id: string;
  external_id: string;
  lead_name: string | null;
  email: string | null;
  source: string | null;
  qualification_score: number | null;
  status: string | null;
  imported_at: string;
}

export default function Pipeline() {
  const [funnelEvents, setFunnelEvents] = useState<FunnelEvent[]>([]);
  const [importedLeads, setImportedLeads] = useState<ImportedLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'leads' | 'opportunities'>('all');
  const [search, setSearch] = useState("");
  const { mapStageForClient, getStageNameForClient, isLoading: mappingsLoading } = useAllStageMappings();

  useEffect(() => {
    const fetchPipelineData = async () => {
      setLoading(true);
      try {
        const [eventsRes, leadsRes] = await Promise.all([
          supabase
            .from('funnel_events')
            .select('id, client_id, ghl_contact_id, ghl_opportunity_id, contact_name, stage, deal_value_cents, setter_id, setter_name, lead_created_at, created_at')
            .order('created_at', { ascending: false })
            .limit(200),
          supabase
            .from('imported_leads')
            .select('id, client_id, external_id, lead_name, email, source, qualification_score, status, imported_at')
            .order('imported_at', { ascending: false })
            .limit(200),
        ]);

        if (eventsRes.error) throw eventsRes.error;
        if (leadsRes.error) throw leadsRes.error;

        setFunnelEvents(eventsRes.data || []);
        setImportedLeads(leadsRes.data || []);
      } catch (error) {
        console.error('Error fetching pipeline data:', error);
        toast.error('Error loading pipeline data');
      } finally {
        setLoading(false);
      }
    };

    fetchPipelineData();
  }, []);

  // Deduplicate funnel events by ghl_opportunity_id (keep latest per opportunity)
  const latestByOpportunity = funnelEvents.reduce((acc, event) => {
    const key = event.ghl_opportunity_id || event.id;
    if (!acc[key] || event.created_at > acc[key].created_at) {
      acc[key] = event;
    }
    return acc;
  }, {} as Record<string, FunnelEvent>);
  const uniqueOpportunities = Object.values(latestByOpportunity);

  // Contacts that have opportunities should only show as opportunities, not leads
  const opportunityContactIds = new Set(uniqueOpportunities.map(e => e.ghl_contact_id));
  const dedupedLeads = importedLeads.filter(l => !opportunityContactIds.has(l.external_id));

  // Helper: capitalize lead names ("theresa" -> "Theresa")
  const capitalizeName = (name: string) =>
    name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

  const stats = {
    leads: dedupedLeads.length,
    opportunities: uniqueOpportunities.length,
    totalValue: uniqueOpportunities.reduce((sum, e) => sum + ((e.deal_value_cents || 0) / 100), 0),
    demosBooked: uniqueOpportunities.filter(e =>
      isCategoryAtOrDeeper(mapStageForClient(e.client_id, e.stage), 'demo_booked')
    ).length,
    closedWon: uniqueOpportunities.filter(e =>
      mapStageForClient(e.client_id, e.stage) === 'closed_won'
    ).length,
  };

  // Build filtered display data
  const filteredData = (() => {
    const mapLead = (l: ImportedLead) => ({
      id: l.id,
      client_id: l.client_id,
      type: 'lead' as const,
      name: capitalizeName(l.lead_name || l.email || 'Unknown'),
      stage: l.status || 'imported',
      value: null as number | null,
      date: l.imported_at,
      source: l.source,
    });

    const mapOpp = (e: FunnelEvent) => ({
      id: e.id,
      client_id: e.client_id,
      type: 'opportunity' as const,
      name: capitalizeName(e.contact_name || e.ghl_contact_id || 'Unknown'),
      stage: e.stage || 'unknown',
      value: e.deal_value_cents ? e.deal_value_cents / 100 : null,
      date: e.created_at,
      source: e.setter_name || (e.setter_id ? e.setter_id : 'GHL'),
    });

    if (filter === 'leads') {
      return dedupedLeads
        .map(mapLead)
        .filter(item => item.name.toLowerCase().includes(search.toLowerCase()));
    }
    if (filter === 'opportunities') {
      return uniqueOpportunities
        .map(mapOpp)
        .filter(item => item.name.toLowerCase().includes(search.toLowerCase()));
    }
    // 'all' -- combine both (opportunities first, then deduped leads)
    return [...uniqueOpportunities.map(mapOpp), ...dedupedLeads.map(mapLead)]
      .filter(item => item.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => b.date.localeCompare(a.date));
  })();

  if (loading || mappingsLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={pipelineSteps} />
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Pipeline</h1>
        <p className="text-muted-foreground">GHL-synced pipeline data</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-500/20">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-3xl font-bold">{stats.leads}</p>
              <p className="text-sm text-muted-foreground">Leads</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-500/20">
              <UserCheck className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <p className="text-3xl font-bold">{stats.opportunities}</p>
              <p className="text-sm text-muted-foreground">Opportunities</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-purple-500/20">
              <CalendarCheck className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-3xl font-bold">{stats.demosBooked}</p>
              <p className="text-sm text-muted-foreground">Demos Booked</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-emerald-500/20">
              <Trophy className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-3xl font-bold">{stats.closedWon}</p>
              <p className="text-sm text-muted-foreground">Closed Won</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-green-500/20">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-3xl font-bold">${stats.totalValue.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Pipeline Value</p>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex gap-2 flex-wrap">
          {(['all', 'leads', 'opportunities'] as const).map((status) => (
            <Button
              key={status}
              variant={filter === status ? "default" : "ghost"}
              size="sm"
              onClick={() => setFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
        <div className="flex-1 max-w-xs">
          <InputVenture
            icon={<Search className="w-4 h-4" />}
            placeholder="Search pipeline..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Pipeline Table */}
      <VentureCard>
        <VentureCardContent>
          {filteredData.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No pipeline data yet</p>
              <p className="text-sm text-muted-foreground mt-2">Connect a GHL account to see synced data.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Stage</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Category
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground inline ml-1 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Venture Hills stage category mapped from GHL pipeline stages</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Value</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Source
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground inline ml-1 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>The setter or lead source for this opportunity</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item) => (
                    <tr key={item.id} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="py-3 px-4 font-medium">{item.name}</td>
                      <td className="py-3 px-4">
                        <BadgeVenture variant={item.type === 'opportunity' ? 'warning' : 'info'}>
                          {item.type}
                        </BadgeVenture>
                      </td>
                      <td className="py-3 px-4">
                        <BadgeVenture variant="default">
                          {item.type === 'opportunity'
                            ? getStageNameForClient(item.client_id, item.stage)
                            : item.stage}
                        </BadgeVenture>
                      </td>
                      <td className="py-3 px-4">
                        {item.type === 'opportunity' ? (() => {
                          const category = mapStageForClient(item.client_id, item.stage);
                          return (
                            <BadgeVenture variant={CATEGORY_BADGE_VARIANTS[category]}>
                              {CATEGORY_LABELS[category]}
                            </BadgeVenture>
                          );
                        })() : (
                          <span className="text-muted-foreground">---</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {item.value != null ? `$${item.value.toLocaleString()}` : '---'}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {new Date(item.date).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {item.source || '---'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>
    </motion.div>
  );
}
